import { toast } from '@heidi/design-system/toast';
import { useIntl } from 'react-intl';

type BillingErrorArgs = {
  title: string;
  /**
   * When the backend returns a **user-facing, pre-authored** `detail.msg`
   * (e.g. "You've already extended your trial this cycle", "This discount
   * code has already been redeemed"), pass it here. When omitted, falls
   * back to the generic "Something went wrong. Please try again."
   *
   * **PHI contract:** the backend billing surface must only populate
   * `detail.msg` with static, human-authored templates that carry no patient
   * data (no names, session content, MRNs, or other PHI). This helper does
   * not attempt to detect or filter PHI on the way through — a length
   * ceiling here would silently drop legitimate long messages while doing
   * nothing to catch short PHI leaks, which is worse than useless. The
   * guarantee lives on the backend contract. If we ever can't hold that
   * contract, the correct next step is a dedicated `user_facing_msg` field
   * on the error envelope that the backend flags per-message.
   */
  description?: string;
};

export function useBillingErrorToast() {
  const intl = useIntl();

  return ({ title, description }: BillingErrorArgs) => {
    toast({
      tone: 'destructive',
      title,
      description:
        description ??
        intl.formatMessage({
          defaultMessage: 'Something went wrong. Please try again.',
          id: 'W/5hwrn09m',
        }),
    });
  };
}
